import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { getApiBase } from '../config/env';

const AUTH_TENANT_ORG_CODE_STORAGE = 'auth_tenant_org_code';

const DEFAULT_BRANDING = {
  display_name: 'HR Workspace',
  short_name: 'Workspace',
  product_label: 'HR Workspace',
  primary_color: '#1f3d7c',
  secondary_color: '#2389ba',
  accent_color: '#75913b',
  logo_light_url: null,
  logo_dark_url: null,
  favicon_url: null,
  email_from_name: 'HR Workspace',
  pdf_header_logo_url: null,
};

const TenantContext = createContext({
  ready: false,
  tenant: null,
  branding: DEFAULT_BRANDING,
  bootstrap: null,
  resolveTenantByOrgCode: async () => null,
});

async function safeJson(response) {
  return response.json().catch(() => null);
}

async function fetchTenantBootstrap(options = {}) {
  const response = await fetch(`${getApiBase()}/public/tenant-bootstrap`, {
    headers: {
      Accept: 'application/json',
    },
    signal: options.signal,
  });

  const payload = await safeJson(response);
  if (!response.ok) {
    throw new Error(payload?.message || 'Unable to resolve tenant branding.');
  }

  return payload;
}

async function fetchTenantBootstrapByOrgCode(orgCode, options = {}) {
  const cleanOrgCode = String(orgCode || '').trim().toLowerCase();
  if (!cleanOrgCode) {
    return null;
  }

  const response = await fetch(`${getApiBase()}/public/tenant-bootstrap/resolve`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ org_code: cleanOrgCode }),
    signal: options.signal,
  });

  const payload = await safeJson(response);
  if (!response.ok) {
    throw new Error(payload?.message || 'Unable to resolve tenant branding.');
  }

  return payload;
}

function applyDocumentIdentity(branding) {
  const displayName = branding?.display_name || DEFAULT_BRANDING.display_name;
  const productLabel = branding?.product_label || DEFAULT_BRANDING.product_label;
  document.title = productLabel || displayName;

  const metaThemeColor = document.querySelector("meta[name='theme-color']");
  if (metaThemeColor) {
    metaThemeColor.setAttribute('content', branding?.primary_color || DEFAULT_BRANDING.primary_color);
  }

  const metaDescription = document.querySelector("meta[name='description']");
  if (metaDescription) {
    metaDescription.setAttribute(
      'content',
      `${displayName} ${productLabel ? `- ${productLabel}` : '- HR workspace'}`.trim()
    );
  }

  const faviconUrl = branding?.favicon_url || `${process.env.PUBLIC_URL || ''}/favicon.ico`;

  let link = document.querySelector("link[rel~='icon']");
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = faviconUrl;
}

export function TenantProvider({ children }) {
  const [ready, setReady] = useState(false);
  const [bootstrap, setBootstrap] = useState(null);

  const applyBootstrap = React.useCallback((payload) => {
    setBootstrap(payload);
    applyDocumentIdentity(payload?.branding || DEFAULT_BRANDING);
  }, []);

  const resolveTenantByOrgCode = React.useCallback(async (orgCode, options = {}) => {
    const payload = await fetchTenantBootstrapByOrgCode(orgCode, options);
    if (payload) {
      applyBootstrap(payload);
    }
    return payload;
  }, [applyBootstrap]);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    const authTenantOrgCode = localStorage.getItem(AUTH_TENANT_ORG_CODE_STORAGE);
    const request = authTenantOrgCode
      ? fetchTenantBootstrapByOrgCode(authTenantOrgCode, { signal: controller.signal }).catch(() =>
          fetchTenantBootstrap({ signal: controller.signal })
        )
      : fetchTenantBootstrap({ signal: controller.signal });

    request
      .then((payload) => {
        if (!active) return;
        applyBootstrap(payload);
      })
      .catch((error) => {
        if (!active) return;
        if (error?.name === 'AbortError') return;
        applyDocumentIdentity(DEFAULT_BRANDING);
      })
      .finally(() => {
        if (active) {
          setReady(true);
        }
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [applyBootstrap]);

  const value = useMemo(() => ({
    ready,
    tenant: bootstrap?.tenant || null,
    branding: {
      ...DEFAULT_BRANDING,
      ...(bootstrap?.branding || {}),
    },
    bootstrap,
    resolveTenantByOrgCode,
  }), [bootstrap, ready, resolveTenantByOrgCode]);

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export function useTenant() {
  return useContext(TenantContext);
}
